import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Users, Award, Calendar, Building2, Shield, Crown, CheckCircle, Clock, Ticket, Search, UserCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const Landing = () => {
  const [featuredExperiences, setFeaturedExperiences] = useState([]);
  const [loadingExperiences, setLoadingExperiences] = useState(true);

  useEffect(() => {
    fetchFeaturedExperiences();
  }, []);

  const fetchFeaturedExperiences = async () => {
    try {
      const response = await api.getFeaturedExperiences();
      setFeaturedExperiences(response.data || []);
    } catch (error) {
      // Fallback to sample data if API not available
      setFeaturedExperiences([
        {
          experience_id: 'exp-corporate-gala-2024',
          title: 'Tech Innovation Awards',
          category: 'Corporate',
          description: 'Orchestrated a multi-stage awards ceremony for 500+ industry leaders, coordinating VIP protocols, live production, and seamless vendor integration.',
          status: 'Delivered',
          hero_image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800'
        },
        {
          experience_id: 'exp-government-summit-2024',
          title: 'Africa Economic Forum',
          category: 'Government',
          description: 'Executed a high-security international summit with heads of state, managing complex logistics, diplomatic protocols, and real-time stakeholder coordination.',
          status: 'Delivered',
          hero_image: 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=800'
        },
        {
          experience_id: 'exp-wedding-luxury-2024',
          title: 'A Royal Celebration',
          category: 'Private',
          description: 'Designed and delivered a three-day destination celebration blending cultural tradition with contemporary luxury across multiple venues.',
          status: 'Curated',
          hero_image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=800'
        }
      ]);
    } finally {
      setLoadingExperiences(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Delivered': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Curated': 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30',
      'By Invitation': 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    };
    return styles[status] || styles['Delivered'];
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="landing-page">
      <Navbar />

      {/* Hero Section - Strategic Event Experiences */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1753030768124-dba306907bba"
            alt="Luxury event space"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            <div className="inline-block mb-6">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] px-4 py-2 border border-[#D4AF37]/30 bg-[#D4AF37]/5">
                Premium Event Consultancy
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-medium leading-[0.9] mb-6 tracking-tight">
              Strategic Event
              <br />
              <span className="text-gradient">Experiences, Delivered</span>
              <br />
              with Precision
            </h1>

            <div className="mb-8">
              <p className="text-2xl md:text-3xl font-light text-[#D4AF37] mb-4">
                Crafting Experiences That Matter
              </p>
              <p className="text-lg md:text-xl font-light leading-relaxed text-white/70 max-w-2xl">
                From intimate gatherings to grand celebrations, we transform vision into unforgettable moments.
              </p>
            </div>

            <p className="text-sm md:text-base text-white/50 mb-8 max-w-2xl">
              Over a decade of trusted delivery across corporate, government, and private events.
            </p>

            {/* Client Types */}
            <div className="flex flex-wrap gap-4 mb-12">
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Building2 size={16} className="text-[#D4AF37]" />
                <span>Corporate Events</span>
              </div>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Shield size={16} className="text-[#D4AF37]" />
                <span>Government Functions</span>
              </div>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Crown size={16} className="text-[#D4AF37]" />
                <span>Private Celebrations</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  className="bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-8 py-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] group"
                  data-testid="request-consultation-button"
                >
                  Request a Consultation
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </Button>
              </Link>
              <Link to="/gallery">
                <Button
                  className="bg-transparent border border-white/20 text-white hover:bg-white hover:text-black uppercase tracking-widest font-bold text-xs px-8 py-6 transition-all"
                  data-testid="explore-work-button"
                >
                  Explore Our Work
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Client Trust Indicators */}
      <section className="py-24 border-y border-white/5" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-white/40 text-sm uppercase tracking-wider">Trusted by Industry Leaders</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="text-3xl md:text-4xl font-bold text-[#D4AF37] mb-2">Over a Decade</div>
              <div className="text-sm text-white/60 uppercase tracking-wider">of Excellence</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">500+</div>
              <div className="text-sm text-white/60 uppercase tracking-wider">Events Delivered</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">50+</div>
              <div className="text-sm text-white/60 uppercase tracking-wider">Corporate Partners</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">10+</div>
              <div className="text-sm text-white/60 uppercase tracking-wider">Government Agencies</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Experiences - Read-Only Showcase */}
      <section className="py-32 bg-[#0A0A0F]" data-testid="featured-experiences">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4 block">
              Portfolio
            </span>
            <h2 className="text-4xl md:text-5xl font-normal leading-tight mb-6">Featured Experiences</h2>
            <p className="text-lg font-light leading-relaxed text-white/70 max-w-2xl">
              A selection of signature events we have crafted for distinguished clients
            </p>
          </motion.div>

          {loadingExperiences ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredExperiences.map((experience, index) => (
                <motion.div
                  key={experience.experience_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <div className="bg-[#050505] border border-white/5 rounded-lg overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-500">
                    {/* Image */}
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={experience.hero_image}
                        alt={experience.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      
                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-black/60 backdrop-blur-sm text-[#D4AF37] text-xs rounded-full">
                          {experience.category}
                        </span>
                      </div>
                      
                      {/* Status Badge */}
                      <div className="absolute top-4 right-4">
                        <span className={`px-3 py-1 text-xs rounded-full border ${getStatusBadge(experience.status)}`}>
                          {experience.status}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-medium mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {experience.title}
                      </h3>
                      <p className="text-white/60 text-sm line-clamp-2">
                        {experience.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-12 text-center">
            <Link to="/gallery">
              <Button
                className="bg-transparent border border-white/20 text-white hover:bg-white hover:text-black uppercase tracking-widest font-bold text-xs px-8 py-4 transition-all"
              >
                View Full Portfolio
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Overview - Refined for B2B */}
      <section className="py-32" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4 block">
              Our Expertise
            </span>
            <h2 className="text-4xl md:text-5xl font-normal leading-tight mb-6">Comprehensive Event Solutions</h2>
            <p className="text-lg font-light leading-relaxed text-white/70 max-w-2xl">
              End-to-end consultancy and delivery for organizations that demand excellence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: 'Strategic Event Consultancy',
                description: 'Expert advisory from concept to execution. We align your event objectives with measurable outcomes.',
                icon: Sparkles,
                clients: 'Corporate & Government'
              },
              {
                title: 'Full Event Management',
                description: 'Turnkey production and coordination. Your dedicated team handles every detail with precision.',
                icon: Calendar,
                clients: 'All Sectors'
              },
              {
                title: 'Creative Direction & Design',
                description: 'Bespoke visual identity and experiential design that reflects your brand and objectives.',
                icon: Award,
                clients: 'Brand Activations'
              },
              {
                title: 'VIP & Protocol Services',
                description: 'Specialized services for high-profile events requiring discretion and protocol expertise.',
                icon: Shield,
                clients: 'Government & Private'
              },
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0A0A0F] border border-white/5 hover:border-[#D4AF37]/30 p-8 rounded-lg transition-all group cursor-pointer"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="text-[#D4AF37] mb-4" size={32} />
                <h3 className="text-2xl font-medium mb-3 group-hover:text-[#D4AF37] transition-colors">
                  {service.title}
                </h3>
                <p className="text-white/60 leading-relaxed mb-4">{service.description}</p>
                <span className="text-xs text-[#D4AF37]/70 uppercase tracking-wider">{service.clients}</span>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/services">
              <Button
                className="bg-transparent border border-white/20 text-white hover:bg-white hover:text-black uppercase tracking-widest font-bold text-xs px-8 py-4 transition-all"
                data-testid="explore-services-button"
              >
                Explore All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Phase B Signalling - Coming Soon Teaser */}
      <section className="py-24 bg-gradient-to-b from-[#0A0A0F] to-[#050505]" data-testid="coming-soon-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#D4AF37] text-xs uppercase tracking-wider mb-6">
              <Clock size={14} className="mr-2" />
              Coming Soon
            </span>
            <h2 className="text-3xl md:text-4xl font-normal leading-tight mb-4">
              The Future of Event Experiences
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              We are building new capabilities to enhance how you discover, engage with, and access premium experiences.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Search,
                title: 'Event Discovery',
                description: 'Browse curated experiences and find events that match your interests and standards.',
                status: 'Planned'
              },
              {
                icon: UserCheck,
                title: 'Attendee Engagement',
                description: 'Enhanced tools for guest management, networking, and personalized event experiences.',
                status: 'Planned'
              },
              {
                icon: Ticket,
                title: 'Ticketing & Access',
                description: 'Seamless ticketing, registration, and access control for public and private events.',
                status: 'Planned'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#050505]/50 border border-white/5 rounded-lg p-6 text-center relative overflow-hidden"
              >
                {/* Coming Soon Overlay */}
                <div className="absolute top-3 right-3">
                  <span className="px-2 py-1 bg-white/5 text-white/40 text-xs rounded">
                    {feature.status}
                  </span>
                </div>
                
                <div className="w-14 h-14 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="text-white/40" size={24} />
                </div>
                <h3 className="text-lg font-medium mb-2 text-white/60">{feature.title}</h3>
                <p className="text-white/40 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-white/30 text-sm">
              Interested in early access? <Link to="/contact" className="text-[#D4AF37] hover:underline">Contact us</Link> to learn more.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section - Consultative Focus */}
      <section className="py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1760539618905-879eea01c003"
            alt="Spectacular event"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium leading-tight mb-8">
              Ready to Elevate Your
              <br />
              <span className="text-gradient">Next Event</span>?
            </h2>
            <p className="text-lg md:text-xl font-light leading-relaxed text-white/70 mb-8">
              Partner with Nehpets+ for events that reflect your organization&apos;s standards of excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  className="bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-10 py-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]"
                  data-testid="discuss-event-cta-button"
                >
                  Discuss Your Event
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  className="bg-transparent border border-white/20 text-white hover:bg-white hover:text-black uppercase tracking-widest font-bold text-xs px-10 py-6 transition-all"
                >
                  Learn About Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;